import { Button, Modal } from '@insio/ui-toolkit';
import { HelpItem, HelpSection } from '@/components/help-modal';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import {
  ADMIN_CAPABILITY_KEYS,
  CAPABILITY_KEYS,
  LIMIT_KEYS,
  SIMPLIFIED_MODE_HIDDEN_CAPABILITY_KEYS,
  TIP_KEYS
} from './chat-content';
import { useCanUseAdminPrompts } from './use-can-use-admin-prompts';
import { useAiChatSimplifiedMode } from './use-ai-chat-simplified-mode';

interface ChatHelpModalProps {
  open: boolean;
  onClose: () => void;
}

/** Explains what Tappeer AI can do, how to prompt it, and where it falls short. */
export function ChatHelpModal({ open, onClose }: ChatHelpModalProps) {
  const t = useFeatureTranslation('aiChat');
  const canUseAdminPrompts = useCanUseAdminPrompts();
  const simplifiedMode = useAiChatSimplifiedMode();
  const capabilityKeys = simplifiedMode
    ? CAPABILITY_KEYS.filter(key => !SIMPLIFIED_MODE_HIDDEN_CAPABILITY_KEYS.includes(key))
    : CAPABILITY_KEYS;

  return (
    <Modal open={open} onClose={onClose} size='sm' testId='chat-help'>
      <Modal.Header onClose={onClose} label={t('help.title')} description={t('help.intro')} />
      <Modal.Content size='full' className='flex flex-col gap-lg overflow-y-auto px-lg pb-lg'>
        <HelpSection title={t('help.capabilitiesTitle')}>
          {capabilityKeys.map(key => (
            <HelpItem key={key} icon='check' text={t(`help.capabilities.${key}`)} />
          ))}
        </HelpSection>
        {canUseAdminPrompts && (
          <HelpSection title={t('help.admin.title')}>
            {ADMIN_CAPABILITY_KEYS.map(key => (
              <HelpItem key={key} icon='check' text={t(`help.admin.${key}`)} />
            ))}
          </HelpSection>
        )}
        <HelpSection title={t('help.tipsTitle')}>
          {TIP_KEYS.map(key => (
            <HelpItem key={key} icon='check' text={t(`help.tips.${key}`)} />
          ))}
        </HelpSection>
        <HelpSection title={t('help.limitsTitle')}>
          {LIMIT_KEYS.map(key => (
            <HelpItem key={key} icon='circle-info' text={t(`help.limits.${key}`)} />
          ))}
        </HelpSection>
      </Modal.Content>
      <Modal.Footer>
        <Button variant='primary' onClick={onClose}>
          {t('help.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
